import React, { useState } from 'react';
import { View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { currentFinancialYear, inr, P, type TaxDeclaration, type TaxProof } from '@hrms/shared';
import { useAuth } from '../../../auth/AuthProvider';
import { api } from '../../../lib/api';
import { openFile } from '../../../lib/files';
import { useDebounced } from '../../../lib/hooks';
import { useMut } from '../../../lib/query';
import { Badge, Button, Card, DataTable, Modal, PageHeader, Row, Screen, Select, Stack, StatusBadge, Text, TextField, useTheme } from '../../../ui';
import { FySelect, NumField } from '../components/common';
import { PersonCell } from '../components/EmployeePicker';

function ProofRow({ p, canVerify }: { p: TaxProof; canVerify: boolean }) {
  const { colors } = useTheme();
  const [amt, setAmt] = useState<number | null>(p.amount);
  const [remarks, setRemarks] = useState(p.remarks ?? '');
  const verify = useMut(
    (status: 'approved' | 'rejected') => api.tax.verifyProof(p.id, { status, remarks: remarks || undefined, approved_amount: status === 'approved' ? amt ?? 0 : undefined }),
    { invalidate: [['tax']], success: (_r, s) => `Proof ${s}` },
  );
  return (
    <View style={{ borderWidth: 1, borderColor: colors.border, borderRadius: 10, padding: 12, gap: 10 }} testID={`proof-${p.id}`}>
      <Row justify="space-between">
        <View style={{ flex: 1, minWidth: 160 }}>
          <Text weight="semibold">
            {p.section} · <Text>{p.file_name}</Text>
          </Text>
          <Text variant="small">{`Claimed ${inr(p.amount)}`}</Text>
        </View>
        <StatusBadge status={p.status} />
        <Button size="sm" icon="eye" title="View file" onPress={() => void openFile(`/tax/proofs/${p.id}/file`)} />
      </Row>
      {p.status === 'pending' && canVerify && (
        <Row gap={8} align="flex-end">
          <NumField label="Approved amount ₹" value={amt} allowEmpty onChange={setAmt} width={150} />
          <TextField label="Remarks" placeholder="Optional" value={remarks} onChangeText={setRemarks} containerStyle={{ flex: 1, minWidth: 160 }} />
          <Button size="sm" variant="success" style={{ alignSelf: 'flex-end', marginBottom: 5 }} icon="check" title="Approve" loading={verify.isPending && verify.variables === 'approved'} onPress={() => verify.mutate('approved')} testID={`approve-proof-${p.id}`} />
          <Button size="sm" variant="danger" style={{ alignSelf: 'flex-end', marginBottom: 5 }} icon="x" title="Reject" loading={verify.isPending && verify.variables === 'rejected'} onPress={() => verify.mutate('rejected')} />
        </Row>
      )}
      {!!p.remarks && p.status !== 'pending' && <Text variant="small">{p.remarks}</Text>}
    </View>
  );
}

const total = (d: TaxDeclaration) => d.items.reduce((a, i) => a + (i.declared_amount || 0), 0);
const pending = (d: TaxDeclaration) => (d.proofs ?? []).filter((p) => p.status === 'pending').length;

export function DeclarationsScreen() {
  const { can } = useAuth();
  const manage = can(P.PAYROLL_MANAGE);
  const [fy, setFy] = useState(currentFinancialYear());
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const ds = useDebounced(search);
  const q = useQuery({ queryKey: ['tax', 'declarations', fy, status, ds, page], queryFn: () => api.tax.declarations({ fy, status: status || undefined, search: ds || undefined, page }) });
  const [selId, setSelId] = useState<number | null>(null);
  const cur = selId ? q.data?.data.find((d) => d.id === selId) ?? null : null;

  return (
    <Screen refreshing={q.isRefetching} onRefresh={() => q.refetch()} testID="tax-declarations">
      <PageHeader title="Tax declarations & proofs" subtitle="Review employee investment declarations and verify uploaded proofs." />
      <Card>
        <Row gap={12} align="flex-end">
          <View style={{ width: 160 }}>
            <FySelect
              label="Financial year"
              value={fy}
              onChange={(v) => {
                setFy(v);
                setPage(1);
              }}
            />
          </View>
          <View style={{ width: 160 }}>
            <Select<string>
              label="Status"
              value={status}
              onChange={(v) => {
                setStatus(v);
                setPage(1);
              }}
              options={[
                { value: '', label: 'All' },
                { value: 'draft', label: 'Draft' },
                { value: 'submitted', label: 'Submitted' },
                { value: 'locked', label: 'Locked' },
              ]}
            />
          </View>
          <TextField
            label="Search"
            placeholder="Employee name or code…"
            value={search}
            onChangeText={(v) => {
              setSearch(v);
              setPage(1);
            }}
            containerStyle={{ flex: 1, minWidth: 180 }}
          />
        </Row>
      </Card>
      <DataTable
        rows={q.data}
        loading={q.isLoading}
        onPageChange={setPage}
        onRowPress={(d) => setSelId(d.id)}
        emptyTitle="No declarations"
        columns={[
          { key: 'employee', title: 'Employee', width: 200, flex: 2, render: (d) => <PersonCell emp={d.employee} fallback={d.employee_id} />, sortValue: (d) => d.employee?.full_name },
          { key: 'regime', title: 'Regime', width: 90, render: (d) => <Badge tone={d.regime === 'new' ? 'info' : 'neutral'}>{d.regime}</Badge>, sortValue: (d) => d.regime },
          { key: 'declared', title: 'Declared', width: 120, align: 'right', render: (d) => inr(total(d)), sortValue: total },
          { key: 'proofs', title: 'Proofs', width: 110, align: 'right', render: (d) => (pending(d) ? <View><Badge tone="warning">{`${pending(d)} pending`}</Badge></View> : String(d.proofs?.length ?? 0)), sortValue: pending },
          { key: 'status', title: 'Status', width: 110, render: (d) => <StatusBadge status={d.status} />, sortValue: (d) => d.status },
        ]}
      />
      <Modal open={!!cur} onClose={() => setSelId(null)} title={cur?.employee?.full_name ?? 'Declaration'} subtitle={cur ? `FY ${cur.financial_year} · ${cur.regime} regime` : undefined} width={680} testID="declaration-sheet">
        {cur && (
          <Stack gap={14}>
            <DataTable
              rows={cur.items}
              keyExtractor={(_, i) => i}
              emptyTitle="No items declared"
              columns={[
                { key: 'section', title: 'Section', width: 90 },
                { key: 'description', title: 'Description', width: 180, flex: 2 },
                { key: 'declared_amount', title: 'Declared', width: 110, align: 'right', render: (i) => inr(i.declared_amount), sortValue: (i) => i.declared_amount },
                { key: 'approved_amount', title: 'Approved', width: 110, align: 'right', render: (i) => inr(i.approved_amount), sortValue: (i) => i.approved_amount },
              ]}
            />
            <Text variant="title">Proofs</Text>
            {(cur.proofs ?? []).length === 0 && <Text variant="muted">No proofs uploaded.</Text>}
            {(cur.proofs ?? []).map((p) => (
              <ProofRow key={p.id} p={p} canVerify={manage} />
            ))}
          </Stack>
        )}
      </Modal>
    </Screen>
  );
}
